import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useAppStore, type Permissions } from './app-store';
import { DEV_AUTH_TOKEN, getDevAuthToken } from '@/lib/dev-auth';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  avatar?: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ user: User; permissions?: Permissions }>;
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
  setUser: (user: User | null, permissions?: Permissions) => void;
  updateUser: (user: Partial<User>) => void;
  updatePermissions: (permissions: Permissions) => void;
  getToken: () => string | null;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3005';

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,

      login: async (email: string, password: string) => {
        const response = await fetch(`${API_URL}/api/trpc/auth.login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            json: { email, password },
          }),
        });

        const data = await response.json();

        if (data.error) {
          throw new Error(data.error.message || 'Invalid email or password');
        }

        const result = data.result?.data?.json;
        if (!result?.user) {
          throw new Error('Login failed - no user data returned');
        }

        const user: User = {
          id: result.user.id,
          email: result.user.email,
          firstName: result.user.firstName,
          lastName: result.user.lastName,
          role: result.user.role,
          avatar: result.user.avatar,
        };

        set({
          user,
          token: result.token,
          isAuthenticated: true,
          isLoading: false,
        });

        // Set permissions if returned
        if (result.permissions) {
          useAppStore.getState().setPermissions(result.permissions);
        }

        return { user, permissions: result.permissions };
      },

      logout: async () => {
        const token = get().token;
        if (token && token !== DEV_AUTH_TOKEN) {
          try {
            await fetch(`${API_URL}/api/trpc/auth.logout`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                json: { token },
              }),
            });
          } catch (error) {
            console.error('Logout error:', error);
          }
        }

        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });

        // Clear app state on logout
        useAppStore.getState().clearAppState();
      },

      checkSession: async () => {
        const storedToken = get().token;
        const token = getDevAuthToken(API_URL) ?? (storedToken === DEV_AUTH_TOKEN ? null : storedToken);
        if (!token) {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
          return;
        }

        try {
          const response = await fetch(`${API_URL}/api/trpc/auth.me?input=${encodeURIComponent(JSON.stringify({ json: { token } }))}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          });

          const data = await response.json();
          const result = data.result?.data?.json;

          if (result?.id) {
            const user: User = {
              id: result.id,
              email: result.email,
              firstName: result.firstName,
              lastName: result.lastName,
              role: result.role,
              avatar: result.avatar,
            };

            set({
              user,
              token,
              isAuthenticated: true,
              isLoading: false,
            });

            // Set permissions if returned
            if (result.permissions) {
              useAppStore.getState().setPermissions(result.permissions);
            }
          } else {
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        } catch (error) {
          console.error('Session check error:', error);
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      },

      setUser: (user, permissions) => {
        set({
          user,
          isAuthenticated: !!user,
          isLoading: false,
        });

        if (permissions) {
          useAppStore.getState().setPermissions(permissions);
        }
      },

      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),

      updatePermissions: (permissions) => {
        useAppStore.getState().setPermissions(permissions);
      },

      getToken: () => get().token,
    }),
    {
      name: 'polatkan-auth',
      // A bypass identity must not survive outside the enabled development server.
      partialize: (state) => state.token === DEV_AUTH_TOKEN ? {
        user: null,
        token: null,
        isAuthenticated: false,
      } : ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
